import { forwardRef, useEffect, useRef } from "react";

import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import OutlinedInput from "@mui/material/OutlinedInput";
import { ON_INPUT_INVALID_DECIMAL, ON_INPUT_INVALID_INTEGER, validateDecimal, validateDecimalOrEmpty, validateInteger, validateIntegerOrEmpty } from "../utils/validation";
import { inputValueToDecimal, inputValueToDecimalOrNull, inputValueToInt, inputValueToIntOrNull } from "../utils/utils";
import { stripTrailingZeros } from "../utils/decimal";

// テキストボックスのFormControl
export const CustomFormControl = forwardRef(({value, setValue, error, helperText, inputSx, onFocusFormat=(v)=>v, onBlurFormat=(v)=>v, helperTextSx, ...props}, ref) => {

    const inputRef = useRef(null);
  
    useEffect(()=> {
      inputRef.current.value = value;
    } , [value])    
  
    const handleFocus = (event) => {
      inputRef.current.value = onFocusFormat(value)
      inputRef.current.select();
    }
  
    const handleBlur = (event) => {
      let newValue = onBlurFormat(inputRef.current.value)
      inputRef.current.value = newValue
      setValue(newValue)
    }
  
    return (
      <FormControl error={error}>
        <OutlinedInput
          type='text'
          onFocus={handleFocus} onBlur={handleBlur}
          ref={ref}
          inputRef={inputRef}
          sx={{...inputSx}}
          {...props}
        />
        <FormHelperText sx={helperTextSx}>{helperText ? helperText : ""}</FormHelperText>
      </FormControl>
    )
  });

// 整数値FormControl
export const IntegerFormControl = forwardRef(({value, setValue, setValid=(isValid) => null, ...props}, ref) => {


  const onBlurFormat = (value) => {
    
    let inputValue = '';
    let isValid = validateInteger(value)

    if (isValid) {
      inputValue = inputValueToInt(value).toLocaleString();        
    } else {
      inputValue = value
    }

    setValid(isValid);
    return inputValue
  }

  return (
    <CustomFormControl
      value={value} setValue={setValue}
      error={!validateInteger(value)}
      helperText={validateInteger(value) ? "" : ON_INPUT_INVALID_INTEGER}
      onBlurFormat={onBlurFormat}
      ref={ref}
      {...props}
    />
  )
});

// 整数値or空文字FormControl
export const IntegerOrEmptyFormControl = forwardRef(({value, setValue, setValid=(isValid) => null, valueOnEmpty="", a=false, ...props}, ref) => {

  const onBlurFormat = (value) => {
    
    let inputValue = '';

    let isValid = validateIntegerOrEmpty(value)

    if (isValid) {
      let intValue = inputValueToIntOrNull(value);
      inputValue = intValue ? intValue.toLocaleString() : valueOnEmpty;
    } else {
      inputValue = value
    }

    setValid(isValid);
    return inputValue;
  }

  return (
    <CustomFormControl
      value={value} setValue={setValue}
      error={!validateIntegerOrEmpty(value)}
      helperText={validateIntegerOrEmpty(value) ? "" : ON_INPUT_INVALID_INTEGER}
      onBlurFormat={onBlurFormat}
      ref={ref}
      {...props}
    />
  )
});

// Decimal値FormControl
export const DecimalFormControl = forwardRef(({value, setValue, setValid=(isValid) => null, fixed=null, ...props}, ref) => {

  const onBlurFormat = (value) => {
    let isValid = validateDecimal(value);
    setValid(isValid);

    if (isValid) {
      return fixed ? inputValueToDecimal(value).toFixed(fixed) : stripTrailingZeros(value);
    } else {
      return value;
    }
  }

  return (
    <CustomFormControl
      value={value} setValue={setValue}
      error={!validateDecimal(value)}
      helperText={validateDecimal(value) ? "" : ON_INPUT_INVALID_DECIMAL}
      onBlurFormat={onBlurFormat}
      ref={ref}
      {...props}
    />
  )
});

// Decimal値or空白値FormControl
export const DecimalOrEmptyFormControl = forwardRef(({value, setValue, setValid=(isValid) => null, valueOnEmpty="", fixed=null, ...props}, ref) => {

  const onBlurFormat = (value) => {

    if (!validateDecimalOrEmpty(value)) {
      setValid(false);
      return value;
    }

    setValid(true);

    const decimalValue = inputValueToDecimalOrNull(value);

    if (decimalValue) {
      return valueOnEmpty;
    }

    return fixed ? inputValueToDecimal(value).toFixed(fixed) : stripTrailingZeros(value);
  }

  return (
    <CustomFormControl
      value={value} setValue={setValue}
      error={!validateDecimalOrEmpty(value)}
      helperText={validateDecimalOrEmpty(value) ? "" : ON_INPUT_INVALID_DECIMAL}
      onBlurFormat={onBlurFormat}
      ref={ref}
      {...props}
    />
  )
});